import { ApolloClient, metaAI, isInProgress, isNonEmpty } from "neql"
import { useEffect, useMemo, useState } from "react"
import type { PrelabelResponse } from "resha"
import { getValue, hasValue } from "../Models/Client/FetchState"
import { useAIModelAssignmentStatus } from "./useAIModelAssignmentStatus"
import { useNeqlContextProvider } from "./useNeqlContextProvider"

export const usePrelabels = (
    appId: string,
    jobId?: string
): PrelabelResponse => {
    const prelabelStatusFetch = useAIModelAssignmentStatus(
        appId,
        metaAI.Meta_Ai_Assignment_Enum.Prelabel
    )
    const [shouldPoll, setShouldPoll] = useState(false)
    const metaAIGraphQlClient: ApolloClient<unknown> = useNeqlContextProvider(
        "metaAIApp",
        appId
    )
    const skipPrelabelsFetch =
        jobId === undefined ||
        !hasValue(prelabelStatusFetch) ||
        prelabelStatusFetch.value === "unused"
    const { data: prelabels, loading } = metaAI.useGetPrelabelsQuery({
        client: metaAIGraphQlClient,
        variables: {
            jobId: jobId === undefined ? null : Number.parseInt(jobId, 10)
        },
        skip: skipPrelabelsFetch,
        pollInterval: shouldPoll ? 2000 : undefined
    })
    const hasPrelabelInProgress =
        prelabels !== undefined &&
        isNonEmpty(prelabels) &&
        prelabels.get_prelabel.some(isInProgress)

    useEffect(() => {
        setShouldPoll(
            getValue(prelabelStatusFetch) === "active" && hasPrelabelInProgress
        )
    }, [hasPrelabelInProgress, prelabelStatusFetch])

    return useMemo(() => {
        if (skipPrelabelsFetch) {
            return undefined
        }

        if (prelabels === undefined || hasPrelabelInProgress || loading) {
            return "loading"
        }

        return prelabels.get_prelabel ?? []
    }, [hasPrelabelInProgress, loading, prelabels, skipPrelabelsFetch])
}
